import {
  ELoaderSize,
  ETextStyleVariant,
  GeneralManageVehiclesOutlineIcon,
  GeneralMapPinOutlineIcon,
  GeneralSearchIcon,
  Loader,
  RawButton,
  Text,
} from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import useQueryParams from '@/hooks/useQueryParams';
import { IHeaderSearchFilterGuests } from '@/redux/modules/globalHeader';
import { isDeliveryModeFromParams } from '@/utility/queryParams';

interface IDateTab {
  onClick: () => void;
  selectedDates: string | null;
  variant?: 'minimal' | 'enhanced';
}

export const DateTab: React.FC<IDateTab> = ({ onClick, selectedDates, variant }) => {
  const intl = useIntl();

  const label = intl.formatMessage({
    defaultMessage: 'Dates',
    id: 'TUk12c',
    description: 'Home Search Widget > Dates Filter Label',
  });

  if (variant === 'enhanced') {
    return (
      <button
        type="button"
        onClick={onClick}
        className="flex flex-col px-4 text-base text-left cursor-pointer justify-left border-neutral-20 text-secondary-base semiHighlight !border-l-0 md:w-48 md:!pl-6 gap-1">
        <Text
          variant={ETextStyleVariant.LegalRegular}
          component="span"
          className="w-full text-gray-500 truncate">
          {label}
        </Text>

        <Text
          variant={ETextStyleVariant.LargeRegular}
          component="span"
          className="w-full text-primary-base">
          {selectedDates ? (
            selectedDates
          ) : (
            <FormattedMessage defaultMessage="Add dates" id="Fzp8xF" />
          )}
        </Text>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'flex items-center justify-center h-full px-4 text-base border-l cursor-pointer border-neutral-20 text-secondary-base semiHighlight md:min-w-[115px]',
        {
          ['!border-l-0 md:w-48 md:!justify-start md:!pl-6']: variant === 'minimal',
        },
      )}>
      {selectedDates ? (
        <span className="font-medium text-primary-base">{selectedDates}</span>
      ) : (
        <span className="font-medium text-gray-800">
          <FormattedMessage defaultMessage="Dates" id="D6b0Au" />
        </span>
      )}
    </button>
  );
};

interface IGuestTabProps {
  onClick: () => void;
  guests?: IHeaderSearchFilterGuests;
  variant?: 'minimal' | 'enhanced';
  isGuestOccupancyAvailable?: boolean;
}

export const GuestsTab: React.FC<IGuestTabProps> = ({
  onClick,
  guests,
  variant,
  isGuestOccupancyAvailable,
}) => {
  const intl = useIntl();

  const totalGuestValue =
    (guests?.adults || 0) +
    (guests?.children || 0) +
    (isGuestOccupancyAvailable ? guests?.infants || 0 : 0);
  const totalGuests = totalGuestValue || 0;

  const buttonLabel = intl.formatMessage(
    {
      defaultMessage: 'Click to {hasValue, select, true {change the} other {add}} guests',
      id: 'PB9EPa',
      description: 'Global Header > Search',
    },
    {
      hasValue: !!totalGuests,
    },
  );
  const placeholder =
    variant === 'enhanced'
      ? intl.formatMessage({
          defaultMessage: 'Add guests',
          id: 'atoxqO',
          description: 'Global Header > Placeholder text for header details field',
        })
      : intl.formatMessage({
          defaultMessage: 'Guests',
          id: 'ogcDqT',
          description: 'Global Header > Placeholder text for header details field',
        });
  const detailsValueText = intl.formatMessage(
    {
      defaultMessage: '{totalGuests, plural, one {# guest} other {# guests}}',
      id: 'YstT2S',
      description: 'Placeholder for header guests field',
    },
    {
      totalGuests,
    },
  );

  const label = intl.formatMessage({
    defaultMessage: 'Guests',
    id: 'p7nvv8',
    description: 'Home Search Widget > Guests Filter Label',
  });

  if (variant === 'enhanced') {
    return (
      <button
        type="button"
        onClick={onClick}
        className="flex flex-col px-4 text-base text-left cursor-pointer justify-left border-neutral-20 text-secondary-base semiHighlight !border-l-0 md:w-48 md:!pl-6 gap-1"
        aria-label={buttonLabel}>
        <Text
          variant={ETextStyleVariant.LegalRegular}
          component="span"
          className="w-full text-gray-500 truncate">
          {label}
        </Text>
        <Text
          variant={ETextStyleVariant.LargeRegular}
          component="span"
          className="w-full text-primary-base">
          {!totalGuests ? placeholder : detailsValueText}
        </Text>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'flex items-center justify-center h-full px-4 text-base border-l cursor-pointer border-neutral-20 text-secondary-base semiHighlight md:min-w-[110px]',
        { ['md:w-48 md:!justify-start']: variant === 'minimal' },
      )}
      aria-label={buttonLabel}>
      {!totalGuests ? (
        <span className="font-medium text-gray-800">{placeholder}</span>
      ) : (
        <span className="font-medium text-primary-base">{detailsValueText}</span>
      )}
    </button>
  );
};

interface ISearchButtonTabProps {
  loading: boolean;
  className?: string;
  variant?: 'minimal' | 'enhanced';
}

export const SearchButton: React.FC<ISearchButtonTabProps> = ({ loading, variant }) => {
  if (variant === 'enhanced') {
    return (
      <div className="pl-2 pr-1.5 gap-4 rounded-r-[inherit]">
        <button
          className="flex items-center justify-center h-full bg-gray-800 rounded-full p-7 shrink-0"
          type="submit"
          data-testid="search-submit-btn"
          disabled={loading}>
          {loading ? (
            <Loader size={ELoaderSize.Medium} onDark className="!w-[32px] !h-[32px]" />
          ) : (
            <GeneralSearchIcon width={32} height={32} color="white" />
          )}
        </button>
      </div>
    );
  }

  return (
    <button
      className="flex items-center justify-center w-10 h-10 mr-1 bg-gray-800 rounded-full shrink-0"
      type="submit"
      data-testid="search-submit-btn"
      disabled={loading}>
      {loading ? (
        <Loader size={ELoaderSize.Small} onDark className="!w-[21px] !h-[21px]" />
      ) : (
        <GeneralSearchIcon width={21} height={21} color="white" />
      )}
    </button>
  );
};

interface IModeSwitcherProps {
  onClick: () => void;
  variant?: 'minimal' | 'enhanced';
}

export const ModeSwitcher: React.FC<IModeSwitcherProps> = ({ onClick, variant }) => {
  const intl = useIntl();
  const queryParams = useQueryParams();

  const isDeliveryMode = isDeliveryModeFromParams(queryParams);

  const label = intl.formatMessage({
    defaultMessage: 'Mode',
    id: 'Rv7C18',
    description: 'Mode switcher label',
  });

  const modeText = isDeliveryMode
    ? intl.formatMessage({
        defaultMessage: 'Delivery',
        id: 'Jbkfo4',
        description: 'Delivery mode text',
      })
    : intl.formatMessage({
        defaultMessage: 'Rentals',
        id: 'xDlwkn',
        description: 'Rentals mode text',
      });

  const IconComponent = isDeliveryMode
    ? GeneralMapPinOutlineIcon
    : GeneralManageVehiclesOutlineIcon;

  if (variant === 'enhanced') {
    return (
      <RawButton
        onPress={onClick}
        className="flex flex-col h-full px-4 text-base text-left cursor-pointer justify-left border-neutral-20 text-secondary-base semiHighlight !border-l-0 md:w-48 md:!pl-6 gap-1">
        <Text
          variant={ETextStyleVariant.LegalRegular}
          component="span"
          className="w-full text-gray-500 truncate">
          {label}
        </Text>

        <div className="flex items-center gap-2">
          <IconComponent className="w-4 h-4" />
          <Text
            variant={ETextStyleVariant.LargeRegular}
            component="span"
            className="w-full text-primary-base">
            {modeText}
          </Text>
        </div>
      </RawButton>
    );
  }

  return (
    <RawButton
      onPress={onClick}
      className={clsx(
        'flex items-center justify-center h-full px-4 text-base border-r cursor-pointer border-neutral-20 text-secondary-base semiHighlight',
        {
          ['!border-l-0 md:w-48 md:!justify-start md:!pl-6']: variant === 'minimal',
        },
      )}>
      <IconComponent fontSize={24} className="text-gray-800" />
    </RawButton>
  );
};
